import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import cln from 'classnames';
import {
  Banners,
  useZustandLanguagesKitStore,
  languageSelectors,
  useTranslate,
} from 'react-gldn-kit';
import { AuthInfo } from 'react-memory-optimization/dist/lib/store/user/types';
import { ECasinoCategoryIds } from 'react-memory-optimization/dist/lib/store/casino/entities/categories/types';
import { useBalance, useAuth } from 'react-render-hooks';
import { BalanceInfo } from 'react-memory-optimization/dist/lib/store/user/entities/balance/types';
import { Banner } from 'react-gldn-kit/dist/lib/components/Banners/types';

import HomeCategories from './components/HomeCategories';
import CasinoCategories from 'components/CasinoComponents/CasinoCategories';

import { APP_TYPE } from 'constants/ui';

import { useZustandWebConfiguration } from 'stores/configuration/store';
import {
  getAdditionalCategoryInfoSelector,
  getHomeBannersSelector,
} from 'stores/configuration/selectors';

import { useZustandModalStore } from 'stores/modal/store';
import { setModalInfoAction } from 'stores/modal/selectors';
import { useZustandDeviceStore } from 'stores/device/store';
import { isMobileDeviceSelector } from 'stores/device/selectors';

import { EBannerTypes } from './types';
import { ERouteNames } from 'Routes/types';
import { ModalTypes } from 'stores/modal/types';

import { useHookFabric } from 'hooks/useHookFabric';

import styles from './Home.module.css';
import animation from 'styles/animation.module.css';
import './sliderHome.css';

const CasinoHome = () => {
  const { getText } = useTranslate('', '');

  const navigate = useNavigate();

  const isMobile = useZustandDeviceStore(isMobileDeviceSelector);

  const setModal = useZustandModalStore(setModalInfoAction);

  const activeLang = useZustandLanguagesKitStore(
    languageSelectors.getActiveLanguageNameKitSelector
  );

  const banners = useZustandWebConfiguration(getHomeBannersSelector);

  const isAuthInfo = useHookFabric<AuthInfo>({
    fn: useAuth,
  });

  const balanceInfo = useHookFabric<BalanceInfo>({
    fn: useBalance,
  });

  const additionalCategoryInfo = useZustandWebConfiguration(
    getAdditionalCategoryInfoSelector
  );

  const handleOpenTG = (id: string) => {
    const banner = banners.find((b) => b.id === id);

    if (!banner?.redirect || !('redirect' in banner)) {
      return;
    }

    const link = banner.redirect[activeLang] || banner.redirect.default;

    const a = document.createElement('a');

    a.href = link;
    a.target = '_blank';
    a.click();
  };

  const handleBannerClick = (id: keyof typeof EBannerTypes) => () => {
    switch (id) {
      case 'Asian':
        additionalCategoryInfo &&
          navigate(
            ERouteNames.CasinoProviders.replace(
              ':providerNames',
              additionalCategoryInfo.providerCategoryNames
            )
              .replaceAll(':page', '0')
              .replaceAll(':scroll', '0')
          );
        break;
      case 'Betting':
      case 'IPL':
        navigate(ERouteNames.FrameBetting);
        break;
      case 'TelegramBot':
        return handleOpenTG(id);
      case 'Originals':
        navigate(
          ERouteNames.CasinoCategories.replaceAll(
            ':categoryId',
            `${ECasinoCategoryIds.Turbo}`
          )
            .replace(':subCategoryId', 'all')
            .replaceAll(':page', '1')
            .replaceAll(':scroll', '0')
        );

        break;
      case 'Referral':
        isAuthInfo
          ? navigate(ERouteNames.ReferralProgram)
          : setModal(ModalTypes.SignIn);
        break;
      case 'Welcome':
        setModal(isAuthInfo ? ModalTypes.Bonuses : ModalTypes.SignIn);
        break;
      case 'DownloadApp':
        navigate(ERouteNames.Applications);
        break;
      case 'BattlesBanner':
        navigate(ERouteNames.Battles);
        break;
      case 'Withdrawals':
        setModal(ModalTypes.MoneyOperations);
        break;

      default:
        break;
    }
  };

  const adaptedBanners = useMemo(() => {
    const { successDepositCnt = 0 } = balanceInfo || {};

    return banners.filter((banner) => {
      if ('depositDependency' in banner) {
        const { depositDependency } = banner as Banner & {
          depositDependency: number;
        };

        return depositDependency > successDepositCnt;
      }

      return true;
    });
  }, [getText, balanceInfo, isMobile]);

  return (
    <>
      <div className={cln(styles.wrapperCasinoHome, animation.animationFadeIn)}>
        <CasinoCategories type="buttons" withoutSubCategory withoutSearch />

        <div className={cln(styles.wrapperSlider, styles.width)}>
          <Banners
            appType={APP_TYPE}
            isAuth={Boolean(isAuthInfo)}
            isMobile={isMobile}
            banners={adaptedBanners}
            handleClick={handleBannerClick}
            language={activeLang}
          />
        </div>

        <HomeCategories />
      </div>
    </>
  );
};

export default CasinoHome;
